import { useState, useEffect, useCallback } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { POSITIONS, OUTWORDS } from "../constants";
import { getMultiplierInfo, calcPoints, calcLevel, playCorrectSound, playIncorrectSound } from "../utils";
import { fetchScenarioFromBank } from "../questionBank";
import { Diamond } from "./Diamond";
import { Celebration, LevelUpCelebration } from "./Celebration";

const normalizeScenario = s => ({...s, correctIndices: s.correctIndices || (s.correctIndex!==undefined?[s.correctIndex]:[0])});

// ── Play Screen ────────────────────────────────────────────────────────────
// When `scenarioQueue` is provided (coach-assigned custom scenarios), the
// player works through that fixed list in order instead of pulling random
// questions from the bank, and the session auto-ends when the queue empties.

function PlayScreen({ info, position, onBack, onLogPlay, onEndSession, userId, scenarioQueue }) {
  const isQueueMode = Array.isArray(scenarioQueue) && scenarioQueue.length>0;
  const [queueIndex,setQueueIndex]=useState(0);
  const [scenario,setScenario]=useState(null),[loading,setLoading]=useState(true),[error,setError]=useState(null);
  const [chosen,setChosen]=useState(null),[submitted,setSubmitted]=useState(false);
  const [score,setScore]=useState(0),[streak,setStreak]=useState(0),[correctCount,setCorrectCount]=useState(0),[total,setTotal]=useState(0);
  const [lastPts,setLastPts]=useState(0),[showPts,setShowPts]=useState(false);
  const [showCelebration,setShowCelebration]=useState(false);
  const [showLevelUp,setShowLevelUp]=useState(false),[levelUpNum,setLevelUpNum]=useState(null);
  const [baseCorrect,setBaseCorrect]=useState(0);
  const [shownIds,setShownIds]=useState([]);
  const [wrongAnswers,setWrongAnswers]=useState([]);
  const [maxStreak,setMaxStreak]=useState(0);

  const scenarioPosition = scenario?.position || position;
  const posInfo=POSITIONS.find(p=>p.id===scenarioPosition);

  useEffect(()=>{
    if(!userId)return;
    getDocs(query(collection(db,"playLogs"),where("userId","==",userId),where("correct","==",true)))
      .then(snap=>setBaseCorrect(snap.size)).catch(()=>{});
  },[userId]);

  const load=useCallback(async()=>{
    if(isQueueMode){
      if(queueIndex>=scenarioQueue.length){
        onEndSession?.({position,total,correct:correctCount,wrongAnswers,maxSessionStreak:maxStreak});
        return;
      }
      setChosen(null);setSubmitted(false);
      setScenario(normalizeScenario(scenarioQueue[queueIndex]));
      setQueueIndex(i=>i+1);
      setLoading(false);
      return;
    }
    setLoading(true);setError(null);setChosen(null);setSubmitted(false);setScenario(null);
    try{
      const s=await fetchScenarioFromBank(position,info.level,shownIds);
      if(s.id)setShownIds(prev=>[...prev,s.id]);
      setScenario(s);
    }catch{setError("Couldn't load a scenario. Check your connection.");}
    setLoading(false);
  },[position,info,shownIds,isQueueMode,queueIndex,scenarioQueue,onEndSession,total,correctCount,wrongAnswers,maxStreak]);

  useEffect(()=>{load();},[]);// eslint-disable-line

  const currentLevel   = calcLevel(baseCorrect+correctCount);
  const correctIndices = scenario?.correctIndices||(scenario?.correctIndex!==undefined?[scenario.correctIndex]:[]);
  const multInfo       = getMultiplierInfo(streak);
  const canSubmit      = !submitted&&scenario&&chosen!==null;
  const isCorrect      = submitted&&correctIndices.includes(chosen);
  const accuracy       = total>0?Math.round((correctCount/total)*100):null;

  const handleSubmit=()=>{
    if(submitted||!scenario||chosen===null)return;
    const ci=scenario.correctIndices||(scenario.correctIndex!==undefined?[scenario.correctIndex]:[0]);
    setSubmitted(true); setTotal(t=>t+1);
    const correct=ci.includes(chosen);
    onLogPlay?.(scenarioPosition,correct,scenario);
    if(correct){
      playCorrectSound();
      const ns=streak+1; setStreak(ns);
      const newMax=Math.max(maxStreak,ns); setMaxStreak(newMax);
      const prevLevel=calcLevel(baseCorrect+correctCount);
      const newCC=correctCount+1; const newLevel=calcLevel(baseCorrect+newCC);
      setCorrectCount(newCC);
      const pts=calcPoints(ns); setScore(s=>s+pts); setLastPts(pts); setShowPts(true);
      if(newLevel>prevLevel){setLevelUpNum(newLevel);setShowLevelUp(true);setTimeout(()=>setShowPts(false),3500);}
      else{setShowCelebration(true);setTimeout(()=>setShowPts(false),2400);}
    }else{
      playIncorrectSound();
      setStreak(0);
      setWrongAnswers(prev=>[...prev,{scenario,chosen}]);
    }
  };

  return (
    <div style={{padding:"14px 16px 22px"}}>
      {showCelebration&&<Celebration streak={streak} onDone={()=>setShowCelebration(false)}/>}
      {showLevelUp&&levelUpNum&&<LevelUpCelebration level={levelUpNum} onDone={()=>setShowLevelUp(false)}/>}

      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <div style={{display:"flex",alignItems:"center",gap:7}}>
          {posInfo
            ? <><div style={{background:"#231F20",color:"#1FD71D",fontSize:12,fontWeight:700,padding:"3px 10px",borderRadius:5,fontFamily:"monospace"}}>{posInfo.id}</div><span style={{fontSize:11,color:"#888"}}>{posInfo.label}</span></>
            : isQueueMode&&<div style={{background:"#231F20",color:"#1FD71D",fontSize:12,fontWeight:700,padding:"3px 10px",borderRadius:5,fontFamily:"monospace"}}>🎯 Coach Scenario</div>
          }
        </div>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          {multInfo.label&&<span style={{fontSize:10,background:"#fff8e1",color:"#f57f17",padding:"2px 8px",borderRadius:999,fontWeight:700}}>{multInfo.label}</span>}
          <span style={{fontFamily:"monospace",fontSize:18,fontWeight:700,color:"#231F20"}}>{score.toLocaleString()}</span>
          {total>0&&<button onClick={()=>onEndSession?.({position:scenarioPosition,total,correct:correctCount,wrongAnswers,maxSessionStreak:maxStreak})} style={{fontSize:10,color:"white",background:"#c62828",border:"none",borderRadius:5,padding:"3px 9px",cursor:"pointer",fontWeight:600}}>End Session</button>}
          {total===0&&<button onClick={onBack} style={{fontSize:10,color:"#bbb",background:"none",border:"1px solid #e0e0e0",borderRadius:5,padding:"2px 8px",cursor:"pointer"}}>← Back</button>}
        </div>
      </div>

      {isQueueMode&&<div style={{fontSize:10,color:"#888",marginBottom:10}}>Assigned scenario {Math.min(queueIndex,scenarioQueue.length)} of {scenarioQueue.length}</div>}

      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:6,marginBottom:13}}>
        {[{label:"Level",val:`⭐ ${currentLevel}`,hl:true},{label:"Played",val:total},{label:"Correct",val:correctCount},{label:"Accuracy",val:accuracy!==null?`${accuracy}%`:"—"}].map(s=>(
          <div key={s.label} style={{background:s.hl?"#231F20":"#f6f6f6",borderRadius:7,padding:"6px 4px",textAlign:"center"}}>
            <div style={{fontSize:8,color:s.hl?"#5fcf70":"#bbb",textTransform:"uppercase",letterSpacing:"0.04em"}}>{s.label}</div>
            <div style={{fontSize:13,fontWeight:700,color:s.hl?"#1FD71D":"#231F20",fontFamily:"monospace"}}>{s.val}</div>
          </div>
        ))}
      </div>

      {loading&&<div style={{textAlign:"center",padding:"36px 0"}}><div style={{fontSize:28,display:"inline-block",animation:"spin 1.8s linear infinite"}}>⚾</div><div style={{fontSize:13,color:"#999",marginTop:10}}>Generating your play...</div></div>}
      {error&&!loading&&<div style={{textAlign:"center",padding:"20px"}}><div style={{color:"#e53935",fontSize:13,marginBottom:10}}>{error}</div><button onClick={load} style={{padding:"9px 22px",background:"#231F20",color:"#1FD71D",border:"none",borderRadius:7,cursor:"pointer",fontFamily:"monospace"}}>Try Again</button></div>}

      {scenario&&!loading&&(<>
        {(scenario.inning||scenario.teamScore!==undefined)&&(
          <div style={{background:"#231F20",borderRadius:8,padding:"8px 12px",marginBottom:8,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <span style={{fontSize:10,color:"#5fcf70",textTransform:"uppercase",letterSpacing:"0.05em"}}>Inning</span>
              <span style={{fontSize:13,fontWeight:700,color:"#1FD71D",fontFamily:"monospace"}}>{scenario.half==="bottom"?"▼":"▲"} {scenario.inning}</span>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <span style={{fontSize:10,color:"#5fcf70",textTransform:"uppercase",letterSpacing:"0.05em"}}>Score</span>
              {scenario.teamScore>scenario.opponentScore
                ?<span style={{fontSize:13,fontWeight:700,color:"#4caf50",fontFamily:"monospace"}}>Up {scenario.teamScore}-{scenario.opponentScore}</span>
                :scenario.teamScore<scenario.opponentScore
                ?<span style={{fontSize:13,fontWeight:700,color:"#e53935",fontFamily:"monospace"}}>Down {scenario.teamScore}-{scenario.opponentScore}</span>
                :<span style={{fontSize:13,fontWeight:700,color:"#1FD71D",fontFamily:"monospace"}}>Tied {scenario.teamScore}-{scenario.opponentScore}</span>
              }
            </div>
          </div>
        )}

        <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:11}}>
          <span style={{background:"#444",color:"#e3fbe6",fontSize:10,fontWeight:700,padding:"2px 9px",borderRadius:999,fontFamily:"monospace"}}>{OUTWORDS[scenario.outs??0]}</span>
          {!scenario.runners?.length
            ?<span style={{background:"#eee",color:"#888",fontSize:10,padding:"2px 9px",borderRadius:999}}>Bases empty</span>
            :scenario.runners.map(r=><span key={r} style={{background:"#1FD71D",color:"#231F20",fontSize:10,fontWeight:700,padding:"2px 9px",borderRadius:999}}>Runner on {r}</span>)
          }
        </div>

        <div>
          <div style={{fontSize:12,color:"#888",marginBottom:6,lineHeight:1.55}}>{scenario.situation}</div>
          <div style={{fontSize:14,fontWeight:700,color:"#231F20",fontStyle:"italic",lineHeight:1.5,marginBottom:12}}>"{scenario.ballInPlay}"</div>
          {scenario.specialNote&&scenario.specialNote.trim()&&(
            <div style={{background:"#fff8e1",border:"1.5px solid #1FD71D",borderRadius:8,padding:"10px 14px",marginBottom:12,display:"flex",gap:10,alignItems:"flex-start"}}>
              <span style={{fontSize:18,flexShrink:0}}>⚠️</span>
              <div>
                <div style={{fontSize:11,fontWeight:700,color:"#856404",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:3}}>Key situation</div>
                <div style={{fontSize:13,color:"#5d4300",lineHeight:1.55}}>{scenario.specialNote}</div>
              </div>
            </div>
          )}
          <div style={{maxWidth:380,margin:"0 auto 14px"}}>
            <Diamond mode="view" runners={scenario.runners||[]} highlightPos={scenarioPosition} size={380}/>
          </div>
          <div style={{background:"#231F20",color:"#e3fbe6",padding:"11px 14px",borderRadius:8,marginBottom:10,fontSize:14,fontWeight:600,lineHeight:1.5}}>
            {scenario.question}
            {correctIndices.length>1&&<span style={{display:"block",fontSize:11,color:"#8fe39c",fontWeight:400,marginTop:3}}>💡 More than one answer may be correct</span>}
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:14}}>
            {scenario.choices?.map((c,i)=>{
              let bg="white",border="1px solid #e0e0e0",color="#333";
              if(submitted){
                if(correctIndices.includes(i)){bg="#e8f5e9";border="1.5px solid #4caf50";color="#2e7d32";}
                else if(i===chosen){bg="#ffebee";border="1.5px solid #e53935";color="#c62828";}
              }else if(chosen===i){bg="#e3fbe6";border="1.5px solid #231F20";color="#231F20";}
              return <button key={i} onClick={()=>!submitted&&setChosen(i)} style={{textAlign:"left",padding:"11px 14px",background:bg,border,borderRadius:8,cursor:submitted?"default":"pointer",fontSize:14,color,lineHeight:1.5,transition:"all 0.15s",fontFamily:"Georgia,serif"}}>
                {c}
                {submitted&&correctIndices.includes(i)&&" ✓"}
                {submitted&&i===chosen&&!correctIndices.includes(i)&&" ✗"}
              </button>;
            })}
          </div>
        </div>

        {submitted&&(
          <div style={{position:"relative",background:isCorrect?"#e8f5e9":"#ffebee",border:`1px solid ${isCorrect?"#4caf50":"#e53935"}`,borderRadius:9,padding:"11px 14px",marginBottom:13}}>
            {showPts&&<div style={{position:"absolute",top:-32,right:12,background:"#231F20",color:"#1FD71D",fontSize:15,fontWeight:700,padding:"3px 13px",borderRadius:999,fontFamily:"monospace",animation:"floatUp 2.4s ease forwards",whiteSpace:"nowrap"}}>+{lastPts} pts!</div>}
            <div style={{fontSize:14,fontWeight:700,color:isCorrect?"#2e7d32":"#c62828",marginBottom:5}}>{isCorrect?"🎉 Great play!":"📚 Here's the right call:"}</div>
            <div style={{fontSize:12,color:isCorrect?"#388e3c":"#c62828",lineHeight:1.65}}>{scenario.explanation}</div>
          </div>
        )}

        {!submitted
          ?<button onClick={handleSubmit} disabled={!canSubmit} style={{width:"100%",padding:"13px",background:canSubmit?"#231F20":"#ddd",color:canSubmit?"#1FD71D":"#aaa",border:"none",borderRadius:9,cursor:canSubmit?"pointer":"default",fontSize:14,fontWeight:700,fontFamily:"monospace",letterSpacing:"0.06em",textTransform:"uppercase",transition:"all 0.15s"}}>Submit Answer</button>
          :<button onClick={load} style={{width:"100%",padding:"13px",background:"#231F20",color:"#1FD71D",border:"none",borderRadius:9,cursor:"pointer",fontSize:14,fontWeight:700,fontFamily:"monospace",letterSpacing:"0.06em",textTransform:"uppercase"}}>{isQueueMode&&queueIndex>=scenarioQueue.length?"Finish →":"Next Play →"}</button>
        }
      </>)}
    </div>
  );
}

export default PlayScreen;
